export const advancedNumber = value => value !== null && value !== undefined && value !== ''
  && typeof value !== 'boolean' && (typeof value === 'number' || typeof value === 'string')
  && Number.isFinite(Number(value)) ? Number(value) : null;

export const ADVANCED_KEY_METRICS = ['FS_RATING', 'DERIVED_GOALS_ASSISTS', 'DERIVED_GOALS_MINUS_XG',
  'DERIVED_SHOT_ACCURACY', 'DERIVED_SHOT_CONVERSION', 'POSSESSION', 'EXPECTED_GOALS', 'SHOTS_TOTAL', 'SHOTS_ON_TARGET',
  'PASSES_ACCURACY', 'DUELS_EFFICIENCY', 'SAVES_TOTAL', 'GOALS_PREVENTED', 'EXPECTED_ASSISTS'];

export function advancedKeyMetrics(metrics) {
  const featured = ADVANCED_KEY_METRICS.map(key => metrics.find(row => row.id === key)).filter(Boolean);
  return featured.length ? featured : metrics;
}

export function advancedMetricValue(metric, mode, minimumMinutes = 0) {
  if (!metric) return null;
  if (minimumMinutes > 0 && (advancedNumber(metric.minutosConDatos) ?? -1) < minimumMinutes) return null;
  if (metric.operacion === 'porcentaje') {
    return advancedNumber(metric.intentos) === 0 ? null : advancedNumber(metric.porcentaje);
  }
  return advancedNumber(metric[metric.operacion === 'media' ? 'media' : mode]);
}

export function advancedMetricLabel(id, catalog, acta) {
  if (id.startsWith('FUENTE_')) {
    return acta?.estadisticasPartido?.total?.find(row => String(row.idFuente) === id.slice(7))?.nombre;
  }
  return catalog?.tiposMetricas?.find(row => row.id === id)?.label;
}

export function advancedReferenceCode(record) {
  const keys = Object.keys(record?.estadisticasAvanzadas?.actasProcesadas || {});
  return keys.at(-1)?.match(/^actaPartido_+(.+)$/)?.[1];
}

export function matchingAdvancedActa(record, data) {
  const code = advancedReferenceCode(record);
  const source = record?.estadisticasAvanzadas?.fuente;
  return (Array.isArray(data) ? data : [data]).find(acta => acta && code
    && source && record.idEquipo != null
    && String(acta.codActa) === code && String(acta.idTemporada) === String(record.idTemporada)
    && acta.fuente === source
    && [acta.codEquipoLocal, acta.codEquipoVisitante].some(id => String(id) === String(record.idEquipo)));
}

export function advancedRoster(record, acta) {
  if (!acta) return [];
  const home = String(acta.codEquipoLocal) === String(record.idEquipo);
  return (home ? acta.jugadoresLocales : acta.jugadoresVisitantes) || [];
}

export function advancedPlayerIdentity(record, acta) {
  const player = advancedRoster(record, acta).find(row => String(row.codJugador) === String(record.idJugador));
  if (!player) return record;
  const detailed = acta.estadisticasJugadores?.jugadores?.find(row =>
    String(row.codJugador) === String(record.idJugador));
  const position = detailed?.posicion;
  return {
    ...record,
    foto: record.foto || player.foto,
    posicion: record.posicion || (typeof position === 'object' ? position?.name || position?.nombre : position),
  };
}

export function advancedGroups(scope, kind, period, comparisonScope) {
  const data = kind === 'team' ? scope?.[period] : scope;
  const first = kind === 'team' ? data?.aFavor : derivedPlayerMetrics(data);
  const second = kind === 'team' ? data?.enContra : derivedPlayerMetrics(comparisonScope);
  const groups = [...new Set([...Object.keys(first || {}), ...Object.keys(second || {})])];
  return groups.map(id => ({
    id,
    metrics: [...new Set([...Object.keys(first?.[id] || {}), ...Object.keys(second?.[id] || {})])]
      .map(key => ({ id: key, first: first?.[id]?.[key], second: second?.[id]?.[key] })),
  })).filter(group => group.metrics.length);
}

export function derivedPlayerMetrics(scope) {
  if (!scope?.metricas) return {};
  const groups = Object.fromEntries(Object.entries(scope.metricas).map(([key, values]) => [key, { ...values }]));
  const metric = id => Object.values(scope.metricas).map(group => group[id]).find(Boolean);
  const derive = (group, id, firstId, secondId, operation) => {
    const first = metric(firstId), second = metric(secondId);
    if (!first && !second) return;
    const matches = advancedNumber(scope.partidosConDatos);
    const minutes = advancedNumber(scope.minutosConDatos);
    // Equal partial counts do not prove equal match sets: both inputs must cover the whole advanced scope.
    const compatible = matches > 0 && minutes > 0 && [first, second].every(item => item?.operacion === 'suma'
      && advancedNumber(item.partidosConDatos) === matches && advancedNumber(item.minutosConDatos) === minutes
      && advancedNumber(item.suma) !== null && advancedNumber(item.suma) >= 0);
    const a = compatible ? Number(first.suma) : null, b = compatible ? Number(second.suma) : null;
    const percentage = operation === 'percentage';
    const value = compatible ? (percentage ? (b > 0 && a <= b ? 100 * a / b : null)
      : operation === 'difference' ? a - b : a + b) : null;
    groups[group] = { ...groups[group], [id]: {
      derivada: true, operacion: percentage ? 'porcentaje' : 'suma',
      partidosConDatos: compatible ? matches : null, minutosConDatos: compatible ? minutes : null,
      ...(percentage ? { porcentaje: value, aciertos: a, intentos: b } : {
        suma: value, media: value === null ? null : value / matches, por90: value === null ? null : 90 * value / minutes,
      }),
    } };
  };
  derive('creacion', 'DERIVED_GOALS_ASSISTS', 'GOALS', 'ASSISTS_GOAL', 'sum');
  derive('finalizacion', 'DERIVED_GOALS_MINUS_XG', 'GOALS', 'EXPECTED_GOALS', 'difference');
  derive('finalizacion', 'DERIVED_SHOT_ACCURACY', 'SHOTS_ON_TARGET', 'SHOTS_TOTAL', 'percentage');
  derive('finalizacion', 'DERIVED_SHOT_CONVERSION', 'GOALS', 'SHOTS_TOTAL', 'percentage');
  return groups;
}

export function advancedComparisonCandidates(record, records, minimumMinutes = 0, scope = 'total') {
  const minimum = advancedNumber(minimumMinutes);
  if (minimum === null || minimum < 0 || !record?.estadisticasAvanzadas?.fuente) return [];
  return (Array.isArray(records) ? records : []).filter(other =>
    other?.id != null && other.idJugador != null && record.idJugador != null
    && String(other.idJugador) !== String(record.idJugador)
    && ['idCompeticion', 'idGrupo', 'idTemporada'].every(key =>
      other[key] != null && record[key] != null && String(other[key]) === String(record[key]))
    && other.estadisticasAvanzadas?.fuente === record.estadisticasAvanzadas.fuente
    && advancedNumber(other.estadisticasAvanzadas?.[scope]?.partidosConDatos) > 0
    && advancedNumber(other.estadisticasAvanzadas?.[scope]?.minutosConDatos) >= minimum
    && advancedNumber(other.estadisticasAvanzadas?.[scope]?.minutosConDatos) > 0);
}

export function advancedPositionAlternatives(system) {
  const sample = advancedNumber(system?.partidosConOnce);
  return Object.entries(system?.jugadoresPorPuesto || {})
    .filter(([slot]) => /^[1-9]\d*$/.test(slot) && Number(slot) <= 11)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([slot, players]) => {
      const ranked = Object.entries(players).map(([id, count]) => ({ id, count: advancedNumber(count) }))
        .filter(player => player.count > 0)
        .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id));
      const compatible = sample > 0 && ranked.reduce((sum, player) => sum + player.count, 0) <= sample;
      return { slot, players: ranked.map(player => ({ ...player,
        percentage: compatible ? 100 * player.count / sample : null,
      })) };
    }).filter(slot => slot.players.length);
}

export function advancedLineupRows(lineup) {
  const lines = String(lineup?.sistema || '').split('-').map(Number);
  const players = lineup?.jugadores;
  if (!lines.length || lines.some(count => !Number.isInteger(count) || count < 1)
    || lines.reduce((sum, count) => sum + count, 0) !== 10 || players?.length !== 11
    || new Set(players).size !== 11) return [];
  let index = 0;
  return [1, ...lines].map(count => players.slice(index, index += count)).reverse();
}
